import React, { Component } from "react";
import styled from "styled-components";
import {
    CustomiseStepActions,
    CustomiseStepButton,
    CustomiseStepCaption,
    CustomiseStepCard,
    CustomiseStepCardTitle,
    CustomiseStepContent,
    CustomiseStepDivider,
    CustomiseStepHeader,
    CustomiseStepShell,
    CustomiseStepTitle,
    FlexContainer,
    LottieLoadingIndicator,
    PortalCaption,
    PortalMetricLabel
} from "src/components";

import { Screens } from "src/scenes/Customise";
import ColorPicker from "../components/ColorPicker";
import ImageSlider from "../components/ImageSlider";
import { CustomiseContext } from "../CustomiseContext";
import { PhoneContainer } from "src/scenes/Customise/forms/UploadLogo";

export const ColorButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
`;

const ButtonContainer = styled.div`
    width: 6.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
`;

export const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
`;

const BlackCircleDisplay = styled.div`
    width: 34px;
    height: 34px;
    background: #000000 0% 0% no-repeat padding-box;
    border: 1px solid #000000;
    border-radius: 20px;
    display: inline-block;
`;

const WhiteCircleDisplay = styled.div`
    width: 34px;
    height: 34px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #000000;
    border-radius: 20px;
    display: inline-block;
`;

const DefaultColourRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;
export function ScreenSwitch(param) {
    switch (param) {
    case Screens.MAIN:
        return "Quote Screen";
    case Screens.NOTICEBOARD:
        return "Noticeboard Screen";
    case Screens.PLAYLISTS:
        return "Playlists Screen";
    case Screens.GOALS:
        return "Goals Screen";
    case Screens.SPLASH:
        return "Loading Screen";
    case Screens.ARTICLES:
        return "Articles Screen";
    case Screens.BODY_AND_MIND:
        return "Body & Mind Screen";
    case Screens.SURVEY:
        return "Survey Screen";
    default:
        break;
    }
}

class SelectColor extends Component {
    static contextType = CustomiseContext;

    state = {
        timestamp: "",
        loading: false,
        errorMessage: "",
    };

    componentDidMount() {
        //make sure logo image will be updated which the current page rendered
        let currentTime = new Date().getTime();
        this.setState({
            timestamp: currentTime,
            loading: false,
        });
    }

    handleBackgroundChange = (color) => {
        this.context.updateBackgroundColor(color);
        this.context.setBackgroundColorSelected(true);
    };

    handlePrimaryColorChange = (color) => {
        this.context.updatePrimaryColor(color);
        this.context.setPrimaryColorSelected(true);
    };

    handleSecondaryColor = (color) => {
        this.context.updateSecondaryColor(color);
        this.context.setSecondaryColorSelected(true);
    };

    finish = () => {
        // Determine if all three colours have been set by the user yet.
        if (!this.context.backgroundColorSelected ||
            !this.context.primaryColorSelected ||
            !this.context.secondaryColorSelected) {
            this.setState({
                errorMessage: "Please select all three colours first"
            });
        } else {
            //console.log("Finish")
            this.setState({ 
                loading: true,
                errorMessage: ""
            });
            this.context.nextClick();
        }
    };

    render() {
        const { loading, errorMessage } = this.state;

        const { currentScreen, backGroundColor, secondaryColor, primaryColor, companyName, colourPalette,
            currentForm, screenIds, leftScreen, rightScreen, isComplete, assetLogoUrl, currentLogoUrl,
            setBackgroundColorSelected, setPrimaryColorSelected, setSecondaryColorSelected } = this.context;

        return (
            <>
                <CustomiseStepShell>
                    <CustomiseStepContent>
                        <CustomiseStepHeader>
                            <div>
                                <CustomiseStepTitle>Step 2: Colour Selection</CustomiseStepTitle>
                                <CustomiseStepCaption margin="0.25rem 0 0">
                                    Select three colours that represent your business. Use the device preview to see the mobile app update as you make changes.
                                </CustomiseStepCaption>
                            </div>
                        </CustomiseStepHeader>

                        <CustomiseStepCard>
                            <CustomiseStepCardTitle>Select App Colours</CustomiseStepCardTitle>
                            <ColorButtonsContainer>
                                <ButtonContainer>
                                    <ColorPicker color={backGroundColor} isComplete={isComplete}
                                        handleColorChange={this.handleBackgroundChange} setSelectedColour={setBackgroundColorSelected} />
                                    <PortalMetricLabel>Background</PortalMetricLabel>
                                </ButtonContainer>
                                <ButtonContainer>
                                    <ColorPicker color={primaryColor} isComplete={isComplete}
                                        handleColorChange={this.handlePrimaryColorChange} setSelectedColour={setPrimaryColorSelected} />
                                    <PortalMetricLabel>Primary</PortalMetricLabel>
                                </ButtonContainer>
                                <ButtonContainer>
                                    <ColorPicker color={secondaryColor} isComplete={isComplete}
                                        handleColorChange={this.handleSecondaryColor} setSelectedColour={setSecondaryColorSelected} />
                                    <PortalMetricLabel>Secondary</PortalMetricLabel>
                                </ButtonContainer>
                            </ColorButtonsContainer>
                        </CustomiseStepCard>

                        <CustomiseStepCard>
                            <CustomiseStepCardTitle>Default Colours</CustomiseStepCardTitle>
                            <PortalCaption margin="0">
                                Some parts of the app interface will remain white or black by default and cannot be changed.
                            </PortalCaption>
                            <DefaultColourRow>
                                <BlackCircleDisplay />
                                <WhiteCircleDisplay />
                            </DefaultColourRow>
                        </CustomiseStepCard>

                        <CustomiseStepDivider />

                        <PortalCaption margin="0">
                            Click <b>Next </b> to proceed to <b>Step 3:</b> Custom App Icon.
                        </PortalCaption>

                        <CustomiseStepActions>
                            <ButtonsContainer>
                                <CustomiseStepButton onClick={() => this.context.goToStep1()}>
                                    Back
                                </CustomiseStepButton>
                                { loading ?
                                    <FlexContainer width="130px" height="34px" >
                                        <LottieLoadingIndicator containerHeight="2rem" height="7px" width="7px" style={{ minHeight: "2em !important" }}/>
                                    </FlexContainer>
                                    :
                                    <CustomiseStepButton
                                        $variant="primary"
                                        onClick={ this.finish } >
                                        Next
                                    </CustomiseStepButton>
                                }
                            </ButtonsContainer>
                            <PortalCaption color="red" margin="0">
                                {errorMessage}
                            </PortalCaption>
                        </CustomiseStepActions>
                    </CustomiseStepContent>
                    <PhoneContainer>
                        <ImageSlider
                            companyName={companyName}
                            logoTopOffset={45}
                            phoneFrameTopOffset={30}
                            currentScreen={currentScreen}
                            currentForm={currentForm}
                            screenIds={screenIds}
                            leftScreen={leftScreen}
                            rightScreen={rightScreen}
                            colourPalette={colourPalette}
                            assetLogoUrl={currentLogoUrl || assetLogoUrl} />
                    </PhoneContainer>
                </CustomiseStepShell>
            </>
        );
    }
}

export default SelectColor;
